/**
 * Is the portal this tab is running the one the server now serves? (Ryan, 2026-10-01: an editor's tab, open since
 * before a deploy, showed the old Download button and no Dropbox link for a day, and nothing told him to reload.)
 *
 * A production build names its app file by content (static/js/main.<hash>.js), and index.html points at the current
 * one. So: the name this tab loaded, against the name a fresh index.html points at. Different = a deploy happened.
 * Pure helpers here, tested in versionWatch.test.mjs; components/VersionWatch.js does the fetching and the asking.
 */
const MAIN = /\/static\/js\/(main\.[0-9a-f]+\.js)/;

/** The app file an index.html points at, or null (a dev server serves bundle.js: nothing to compare). */
export const bundleIn = (html) => {
  const m = MAIN.exec(typeof html === 'string' ? html : '');
  return m ? m[1] : null;
};

/** The app file this page loaded, from its script tags. */
export const runningBundle = (doc) => {
  const scripts = Array.from(doc?.querySelectorAll?.('script[src]') || []);
  for (const s of scripts) {
    const name = bundleIn(s.getAttribute('src') || '');
    if (name) return name;
  }
  return null;
};

/** True only when both are known and differ: an unreadable answer is never a new version. */
export const isNewVersion = (running, served) => !!running && !!served && running !== served;

export const CHECK_EVERY_MS = 5 * 60 * 1000;   // while the tab is open
export const MIN_GAP_MS = 60 * 1000;           // a focus or a return to the tab checks too, at most once a minute

/* At most one ask a day (Ryan, 2026-10-09: "i do like 10 a day so we should limit to once a day"). The first deploy of
 * the day asks; later ones that day say nothing, and a tab that stays open hears about them the next day. The day is
 * the browser's own calendar day, and the record is the browser's (localStorage), shared by its tabs: so the tabs open
 * when the day's ask went out all ask about that same build, and none asks about a later one until tomorrow. */
export const ASK_KEY = 'ug_version_asked';

/** The browser's calendar day for a time: "2026-10-09". */
export const dayOf = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** May this tab ask about `served` now? `last` is the record of the day's ask ({day, bundle}) or null. Yes when nothing
 *  asked today, or today's ask was about this same build (another tab already asked; this one is just as old). */
export const mayAsk = (last, served, nowMs) =>
  !last || last.day !== dayOf(nowMs) || last.bundle === served;

/** The record read back from storage, or null for anything unreadable (a private window, a cleared or odd value). */
export const readAsked = (raw) => {
  try {
    const v = JSON.parse(raw);
    return v && typeof v.day === 'string' && typeof v.bundle === 'string' ? v : null;
  } catch { return null; }
};
